import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from 'lucide-react';
import { getSharedConfig } from '../../../services/api/sharedConfig.api';

// Mirrors the enabled-filter checks in hooks/useLocalFilters.js's
// hasLocalFilters/localFilterCount — kept as a small ordered table (instead
// of importing that hook, which is React-state-shaped and not meant for a
// one-off read of an already-fetched settings object) so this preview shows
// the exact same set of filter types a tile itself would treat as active.
const LOCAL_FILTER_CHECKS = [
  { key: 'objectTypes', active: (v) => v?.length > 0, count: (v) => v.length },
  { key: 'dateRange', active: (v) => v?.enabled },
  { key: 'eventAttributeFilters', active: (v) => v?.some((f) => f.enabled), count: (v) => v.filter((f) => f.enabled).length },
  { key: 'activityFilters', active: (v) => v?.enabled },
  { key: 'lifecycleActivityFilters', active: (v) => v?.enabled },
  { key: 'leadTimeRange', active: (v) => v?.enabled },
  { key: 'objectAttributePresenceFilters', active: (v) => v?.enabled },
  { key: 'objectAttributeFilters', active: (v) => v?.some((f) => f.enabled), count: (v) => v.filter((f) => f.enabled).length },
  { key: 'variantFilter', active: (v) => v?.enabled },
  { key: 'edgeDurationFilters', active: (v) => v?.length > 0, count: (v) => v.length },
  { key: 'edgeContainmentFilters', active: (v) => v?.enabled },
  { key: 'activityNodeFilters', active: (v) => v?.enabled },
];

const describeLocalFilters = (localFilters, t) => {
  if (!localFilters) return [];
  return LOCAL_FILTER_CHECKS.filter(({ key, active }) => active(localFilters[key])).map(({ key, count }) =>
    t(`assignConfig.filterLabels.${key}`, count ? { count: count(localFilters[key]) } : undefined),
  );
};

const formatSettingValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? '✓' : null;
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : null;
  if (typeof value === 'object') return Object.keys(value).length > 0 ? JSON.stringify(value) : null;
  return String(value);
};

/**
 * Read-only preview of a shared dashboard config: which tabs it defines,
 * which tiles sit in each, and — per tile — the enabled local filters plus
 * any other non-default settings. Fetches the full payload via the same
 * GET /dashboard/shared-configs/{id} endpoint the dashboard itself loads
 * from (admins bypass its tenant-scope check), so no backend change is
 * needed for this admin-only view.
 */
const SharedConfigPreview = ({ configId }) => {
  const { t } = useTranslation('admin');
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setEntry(null);
    setErr(null);
    if (!configId) return undefined;
    setLoading(true);
    getSharedConfig(configId)
      .then((result) => {
        if (!cancelled) setEntry(result);
      })
      .catch((e) => {
        if (!cancelled) setErr(t('assignConfig.previewError', { detail: e.message }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configId, t]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
        <Loader className="w-3.5 h-3.5 animate-spin" /> {t('assignConfig.loadingPreview')}
      </div>
    );
  }

  if (err) {
    return <div className="text-xs text-error-600 mt-2">{err}</div>;
  }

  if (!entry) return null;

  const { tabs, components, component_settings: componentSettings } = entry.config;

  if (!tabs || tabs.length === 0) {
    return <div className="text-xs text-gray-400 mt-2">{t('assignConfig.noTabs')}</div>;
  }

  return (
    <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 bg-gray-50/50">
      {tabs.map((tab) => {
        const tabComponents = (components || []).filter((c) => c.tab === tab.id);
        const tabFilterLabels = describeLocalFilters(tab.tabFilters, t);
        return (
          <div key={tab.id} className="p-2.5">
            <div className="text-xs font-medium text-gray-700">{tab.label}</div>
            {tabFilterLabels.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="text-[11px] text-gray-400">{t('assignConfig.tabFilters')}:</span>
                {tabFilterLabels.map((label) => (
                  <span key={label} className="text-[11px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-600">
                    {label}
                  </span>
                ))}
              </div>
            )}
            {tabComponents.length === 0 ? (
              <div className="text-[11px] text-gray-400 mt-1">{t('assignConfig.noTiles')}</div>
            ) : (
              <ul className="mt-1.5 space-y-1.5">
                {tabComponents.map((comp) => {
                  const settingsKey = comp.instanceId || comp.id;
                  const settings = (componentSettings || {})[settingsKey];
                  const filterLabels = describeLocalFilters(settings?.localFilters, t);
                  const otherEntries = Object.entries(settings || {})
                    .filter(([key]) => key !== 'localFilters')
                    .map(([key, value]) => [key, formatSettingValue(value)])
                    .filter(([, value]) => value !== null);

                  return (
                    <li key={settingsKey} className="text-xs text-gray-600">
                      <span className="text-gray-800">{comp.name}</span>
                      {filterLabels.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          <span className="text-[11px] text-gray-400">{t('assignConfig.filters')}:</span>
                          {filterLabels.map((label) => (
                            <span key={label} className="text-[11px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-600">
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                      {otherEntries.length > 0 && (
                        <div className="text-[11px] text-gray-400 mt-0.5">
                          {t('assignConfig.settings')}:{' '}
                          {otherEntries.map(([key, value]) => `${key}=${value}`).join(', ')}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SharedConfigPreview;
